import {
  ArrowDownTrayIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from "@heroicons/react/24/outline";
import { useMemo, useState } from "react";

import {
  EmailSummary,
  InboundDocumentJob,
} from "#/api/inbound-document-jobs.type";
import { UserRow } from "#/api/user.type";
import BadgeButton from "#/components/shared/BadgeButton";
import ErrorDisplay from "#/components/shared/ErrorDisplay";
import Typography from "#/components/shared/typography";
import { formatTimeStampToHumanReadableShortDateTime } from "#/components/utls/time";
import { useFindUsersByParameters } from "../hooks/useFindUsersByParameters";
import { useMatchInboundDocumentJobUser } from "../hooks/useMatchInboundDocumentJobUser";

type InboundDocumentJobCardProps = {
  job: InboundDocumentJob;
};

function StatusPill({ status }: { status: string }) {
  const normalized = status.toLowerCase();
  const tone =
    normalized === "failed"
      ? "bg-danger/10 text-danger border-danger/30"
      : normalized === "completed" || normalized === "succeeded"
      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
      : "bg-terracotta-50 text-terracotta-800 border-terracotta-200";

  return (
    <span
      className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold ${tone}`}
    >
      {status}
    </span>
  );
}

function DetailRow({
  label,
  value,
  isMuted,
}: {
  label: string;
  value: string | null | undefined;
  isMuted?: boolean;
}) {
  if (!value) return null;
  return (
    <div className="flex flex-col gap-1 rounded-lg bg-cream-50 p-3">
      <Typography variant="caption" className="text-brown-500">
        {label}
      </Typography>
      <Typography
        variant="body2"
        className={isMuted ? "text-brown-500" : "text-brown-700"}
      >
        {value}
      </Typography>
    </div>
  );
}

function getMetadataField(
  metadata: InboundDocumentJob["metadata"],
  keys: string[]
): string | undefined {
  if (!metadata) return undefined;
  for (const key of keys) {
    const value = metadata[key];
    if (typeof value === "string" && value.trim().length > 0) {
      return value;
    }
  }
  return undefined;
}

function getNestedCapabilityUrl(metadata: InboundDocumentJob["metadata"]) {
  if (!metadata) return undefined;
  const capability = metadata["capability"];
  if (capability && typeof capability === "object" && "url" in capability) {
    const candidate = (capability as Record<string, unknown>).url;
    if (typeof candidate === "string") {
      return candidate;
    }
  }
  return undefined;
}

function ensureString(value: unknown): string | null {
  return typeof value === "string" ? value : null;
}

function EmailSummaryDetails({ emailSummary }: { emailSummary: EmailSummary }) {
  const fullName =
    `${emailSummary.patient_first_name} ${emailSummary.patient_last_name}`.trim();
  const addressParts = [
    emailSummary.patient_address,
    [emailSummary.patient_city, emailSummary.patient_state]
      .filter(Boolean)
      .join(", "),
  ].filter((part) => part && part.length > 0);

  return (
    <div className="space-y-2 rounded-xl border border-cream-200 bg-cream-50 p-4">
      <Typography variant="subtitle2" className="text-terracotta-800">
        Email summary
      </Typography>
      <Typography variant="body2" className="text-brown-700">
        {emailSummary.summary}
      </Typography>
      <div className="grid gap-2 text-sm text-brown-700 md:grid-cols-2">
        <span className="font-semibold text-brown-800">Patient:</span>
        <span>{fullName || "—"}</span>
        <span className="font-semibold text-brown-800">DOB:</span>
        <span>{emailSummary.patient_dob || "—"}</span>
        <span className="font-semibold text-brown-800">MRN:</span>
        <span>{emailSummary.patient_mrn || "—"}</span>
        <span className="font-semibold text-brown-800">Phone:</span>
        <span>{emailSummary.patient_phone || "—"}</span>
        <span className="font-semibold text-brown-800">Email:</span>
        <span>{emailSummary.patient_email || "—"}</span>
        <span className="font-semibold text-brown-800">Address:</span>
        <span>{addressParts.length > 0 ? addressParts.join(" • ") : "—"}</span>
      </div>
    </div>
  );
}

export function InboundDocumentJobCard({ job }: InboundDocumentJobCardProps) {
  const [showMetadata, setShowMetadata] = useState(false);
  const [matchedUserId, setMatchedUserId] = useState<string | null>(null);

  const statusLabel = job.inboundDocumentJobStatus ?? job.status ?? "unknown";
  const emailSummary = job.metadata?.email_summary;
  const patientIdFromMetadata =
    ensureString(job.metadata?.patientId) ??
    ensureString(job.metadata?.objectMetadata?.patientId) ??
    ensureString(job.metadata?.objectMetadata?.patient_id) ??
    null;

  const downloadUrl = useMemo(() => {
    return (
      getMetadataField(job.metadata, ["downloadUrl", "signedUrl", "url"]) ||
      getNestedCapabilityUrl(job.metadata)
    );
  }, [job.metadata]);

  const createdAt = job.createdAt
    ? formatTimeStampToHumanReadableShortDateTime(job.createdAt)
    : null;
  const updatedAt = job.updatedAt
    ? formatTimeStampToHumanReadableShortDateTime(job.updatedAt)
    : null;

  const {
    mutate: findUsers,
    data: matchedUsers = [],
    isPending: isFindingUsers,
    isError: isUserSearchError,
    error: userSearchError,
  } = useFindUsersByParameters();
  const {
    mutateAsync: matchUser,
    isPending: isMatchingUser,
    isError: isMatchError,
    error: matchError,
  } = useMatchInboundDocumentJobUser();

  const canSearchUsers =
    Boolean(emailSummary?.patient_email) ||
    Boolean(
      emailSummary?.patient_first_name || emailSummary?.patient_last_name
    );

  const handleFindUsers = () => {
    if (!canSearchUsers) return;
    findUsers({
      email: emailSummary?.patient_email ?? null,
      firstName: emailSummary?.patient_first_name ?? null,
      lastName: emailSummary?.patient_last_name
        ? ensureString(emailSummary.patient_last_name)
        : null,
      patientId: patientIdFromMetadata,
      dateOfBirth: emailSummary?.patient_dob
        ? ensureString(emailSummary.patient_dob)
        : null,
      email_summary: emailSummary
        ? {
            patient_email: emailSummary.patient_email,
            patient_first_name: emailSummary.patient_first_name,
            patient_last_name: emailSummary.patient_last_name,
            patient_dob: emailSummary.patient_dob,
          }
        : undefined,
      metadata: patientIdFromMetadata
        ? { patientId: patientIdFromMetadata as string }
        : undefined,
      objectMetadata:
        job.metadata?.objectMetadata &&
        (job.metadata.objectMetadata.patientId ||
          (job.metadata.objectMetadata as Record<string, unknown>)?.patient_id)
          ? {
              patientId: job.metadata.objectMetadata.patientId ?? null,
              patient_id: (
                job.metadata.objectMetadata as Record<string, unknown>
              )?.patient_id as string | null | undefined,
            }
          : undefined,
    });
  };

  const userDisplayName = (user: UserRow) => {
    const fullName =
      ensureString((user as { full_name?: string | null }).full_name) ?? "";
    const name =
      `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim() || fullName;
    return name || user.email || user.phone || user.id;
  };

  const handleMarkMatch = async (user: UserRow) => {
    try {
      await matchUser({
        jobId: job.id,
        patientUserId: user.id,
        patientId:
          ensureString((user as { patient_id?: string | null }).patient_id) ??
          patientIdFromMetadata ??
          null,
        subjectUserId: user.id,
        statusDetail: "Marked as matched via admin UI",
        metadata: {
          messageId: job.messageId,
        },
      });
      setMatchedUserId(user.id);
    } catch (err) {
      console.error("Unable to mark user as matched", err);
    }
  };

  return (
    <li className="rounded-2xl border border-cream-200 bg-white shadow-sm">
      <div className="flex flex-col gap-4 border-b border-cream-100 p-4 md:flex-row md:items-start md:justify-between">
        <div className="space-y-2">
          <Typography variant="subtitle1" className="text-terracotta-800">
            Job {job.id}
          </Typography>
          <div className="flex flex-wrap items-center gap-2">
            <StatusPill status={statusLabel} />
            {job.errorMessage && (
              <span className="rounded-full bg-danger/10 px-3 py-1 text-xs font-medium text-danger">
                Error: {job.errorMessage}
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-col items-start gap-1 text-sm text-brown-500 md:items-end">
          {createdAt && <span>Created {createdAt}</span>}
          {updatedAt && <span>Updated {updatedAt}</span>}
        </div>
      </div>

      <div className="grid gap-3 p-4 md:grid-cols-2">
        <DetailRow label="Message ID" value={job.messageId} />
        <DetailRow label="Inbound Document ID" value={job.inboundDocumentId} />
        <DetailRow label="Job Reference" value={job.inboundDocumentJobId} />
        <DetailRow label="Job Status" value={statusLabel} />
        <DetailRow
          label="Patient ID (metadata)"
          value={patientIdFromMetadata}
          isMuted={!patientIdFromMetadata}
        />
      </div>

      <div className="space-y-3 border-t border-cream-100 p-4">
        {downloadUrl && (
          <a
            href={downloadUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 text-sm font-semibold text-terracotta-700 hover:text-terracotta-600"
          >
            <ArrowDownTrayIcon className="h-4 w-4" aria-hidden />
            Open document (capability link)
          </a>
        )}

        <div className="flex items-center gap-3">
          <BadgeButton
            tone="primary"
            variant="soft"
            size="sm"
            onClick={() => setShowMetadata((open) => !open)}
            trailingIcon={
              showMetadata ? (
                <ChevronUpIcon className="h-4 w-4" />
              ) : (
                <ChevronDownIcon className="h-4 w-4" />
              )
            }
          >
            {showMetadata ? "Hide metadata" : "View metadata"}
          </BadgeButton>
          {!job.metadata && (
            <Typography variant="caption" className="text-brown-500">
              No metadata attached to this job.
            </Typography>
          )}
        </div>

        {emailSummary ? (
          <EmailSummaryDetails emailSummary={emailSummary} />
        ) : null}

        <div className="space-y-2 rounded-xl border border-cream-200 bg-cream-50 p-3">
          <div className="flex flex-wrap items-center gap-3">
            <Typography variant="subtitle2" className="text-terracotta-800">
              Potential matching users
            </Typography>
            <BadgeButton
              tone="primary"
              variant="solid"
              size="sm"
              onClick={handleFindUsers}
              disabled={!canSearchUsers || isFindingUsers}
            >
              {isFindingUsers ? "Searching…" : "Find users by parameters"}
            </BadgeButton>
            {!canSearchUsers && (
              <Typography variant="caption" className="text-brown-500">
                Provide an email summary to search for users.
              </Typography>
            )}
          </div>

          {isUserSearchError ? (
            <ErrorDisplay
              tone="warning"
              compact
              description={
                userSearchError instanceof Error
                  ? userSearchError.message
                  : "Unable to search for users right now."
              }
            />
          ) : null}

          {!isUserSearchError &&
          matchedUsers.length === 0 &&
          !isFindingUsers ? (
            <Typography variant="body2" className="text-brown-600">
              No matching users returned yet.
            </Typography>
          ) : null}

          {matchedUsers.length > 0 ? (
            <ul className="divide-y divide-cream-200 rounded-lg border border-cream-200 bg-white">
              {matchedUsers.map((user) => (
                <li key={user.id} className="flex flex-col gap-2 p-3">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <Typography
                        variant="subtitle2"
                        className="text-terracotta-800"
                      >
                        {userDisplayName(user)}
                      </Typography>
                      <Typography variant="caption" className="text-brown-600">
                        Email: {user.email && user.email.length > 0 ? user.email : "—"}
                      </Typography>
                      <Typography variant="caption" className="text-brown-600">
                        Patient ID:{" "}
                        {(user as { patient_id?: string | null }).patient_id ??
                          "—"}
                      </Typography>
                      <Typography variant="caption" className="text-brown-600">
                        DOB: {(user as { dob?: string | null }).dob ?? "—"}
                      </Typography>
                    </div>
                    <BadgeButton
                      tone={matchedUserId === user.id ? "success" : "primary"}
                      variant={matchedUserId === user.id ? "soft" : "solid"}
                      size="sm"
                      disabled={Boolean(matchedUserId) || isMatchingUser}
                      onClick={() => handleMarkMatch(user)}
                    >
                      {matchedUserId === user.id
                        ? "Matched"
                        : isMatchingUser
                        ? "Marking…"
                        : "Mark as match"}
                    </BadgeButton>
                  </div>
                  {matchedUserId === user.id && (
                    <Typography variant="caption" className="text-emerald-700">
                      This job has been marked as matched to this user.
                    </Typography>
                  )}
                </li>
              ))}
            </ul>
          ) : null}

          {isMatchError ? (
            <ErrorDisplay
              tone="warning"
              compact
              description={
                matchError instanceof Error
                  ? matchError.message
                  : "Unable to mark the user as matched right now."
              }
            />
          ) : null}
        </div>

        {showMetadata && (
          <pre className="max-h-80 overflow-auto rounded-xl bg-cream-50 p-3 text-xs text-brown-700">
            {JSON.stringify(job.metadata ?? {}, null, 2)}
          </pre>
        )}
      </div>
    </li>
  );
}

export default InboundDocumentJobCard;
